/**
 * Events API endpoints - VizDay events, parsing, conflicts
 */
import { fetchApi } from './apiBase';
import { Event, ParsedEventData } from '../types';

// === Types ===

export interface EventConflict {
  id: number;
  event_one: string;
  event_one_title: string;
  event_two: string;
  event_two_title: string;
  overlap_start: string;
  overlap_end: string;
  overlap_minutes: number;
  resolution: string;
  suggested_solutions: string[];
  user_notified: boolean;
  created_at: string;
}

export interface ConflictResolutionOption {
  id: string;
  label: string;
  description: string;
  action: 'reschedule' | 'cancel' | 'delegate' | 'keep' | 'custom';
}

export interface ConflictResolution {
  conflict_id: number;
  resolution_type: string;
  selected_option: string;
  notes?: string;
  new_time?: string;
}

// === Event Parsing ===

export async function parseEventText(
  text: string,
  inputType: string = 'auto',
  autoCreate: boolean = false
): Promise<{ parsed: ParsedEventData; event?: Event }> {
  return fetchApi('/api/calendars/parse/', {
    method: 'POST',
    body: JSON.stringify({
      text,
      input_type: inputType,
      auto_create: autoCreate,
    }),
  });
}

export async function parseAndCreateEvent(
  text: string,
  inputType: string = 'auto'
): Promise<{ event: Event; parsed: ParsedEventData; conflicts: EventConflict[] }> {
  return fetchApi('/api/calendars/parse/create/', {
    method: 'POST',
    body: JSON.stringify({
      text,
      input_type: inputType,
    }),
  });
}

// === Events CRUD ===

export async function getEvents(
  start?: string,
  end?: string,
  status?: string,
  expand?: boolean
): Promise<{ events: Event[] }> {
  const params = new URLSearchParams();
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  if (status) params.append('status', status);
  if (expand) params.append('expand', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchApi<{ events: Event[] }>(`/api/calendars/events/${query}`);
}

export async function getEvent(id: string): Promise<Event> {
  return fetchApi<Event>(`/api/calendars/events/${id}/`);
}

export async function createEvent(data: Partial<Event>): Promise<Event> {
  return fetchApi<Event>('/api/calendars/events/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEvent(id: string, data: Partial<Event>): Promise<Event> {
  return fetchApi<Event>(`/api/calendars/events/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteEvent(id: string): Promise<void> {
  await fetchApi(`/api/calendars/events/${id}/`, { method: 'DELETE' });
}

export async function confirmEvent(id: string): Promise<Event> {
  return fetchApi<Event>(`/api/calendars/events/${id}/confirm/`, {
    method: 'POST',
  });
}

export async function syncEventToGoogle(id: string): Promise<{ event: Event; google_event_id: string }> {
  return fetchApi(`/api/calendars/events/${id}/sync-to-google/`, {
    method: 'POST',
  });
}

// === Conflicts ===

export async function getEventConflicts(): Promise<{ conflicts: EventConflict[] }> {
  return fetchApi<{ conflicts: EventConflict[] }>('/api/calendars/events/conflicts/');
}

export async function resolveConflict(
  conflictId: number,
  resolution: ConflictResolution
): Promise<{ success: boolean; message: string }> {
  return fetchApi(`/api/calendars/events/conflicts/${conflictId}/resolve/`, {
    method: 'POST',
    body: JSON.stringify(resolution),
  });
}

export async function getConflictResolutionOptions(
  conflictId: number
): Promise<{ options: ConflictResolutionOption[] }> {
  return fetchApi<{ options: ConflictResolutionOption[] }>(
    `/api/calendars/events/conflicts/${conflictId}/options/`
  );
}
